import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import Select, { components } from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { useDispatch, useSelector } from "react-redux";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useNavigate } from "react-router-dom";
import {
  getLetterList,
  downloadLetterQR,
  downloadLetter,
  requestLetterApproval,
  generateFinalLetter,
  deleteLetter,
} from "action/letter";
import { letterList } from "constants/letter";
import {
  LETTER_TYPE_OPTIONS,
  LETTER_SCOPE_FILTER_OPTIONS,
  LETTER_STATUS_OPTIONS,
  getStatusColor,
} from "components/constant";
import DatePicker from "react-datepicker";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CommonModal from "components/CommonComponents/CommonModal";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { showAlert } from "components/CommonComponents/CommonFunctions";

function LetterListing() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const letterDataList = useSelector((state) => state.letters.letterList);

  const [pageNum, setPageNum] = useState(1);
  const [filterState, setFilterState] = useState({
    documentType: "",
    documentScope: "",
    referenceNo: "",
    issuedToCustomer: "",
    project: "",
    requestOf: "",
    agency: "",
    issueDate: [null, null],
    validTill: [null, null],
    status: null,
    filterParams: [],
  });

  const [alternative, setAlternative] = useState({
    isLoading: true,
    showLoaderOnClick: false,
    error: "",
    noRecords: false,
  });

  const [isDownloading, setIsDownloading] = useState({
    QR: false,
    letterGen: false,
  });
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    fileUrl: "",
    status: "",
  });

  const handleRequestApproval = (letterId) => {
    showAlert({
      title: "Are you sure you want to request approval?",
      message: "Once requested, this letter cannot be edited.",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setAlternative((prev) => ({
            ...prev,
            showLoaderOnClick: true,
          }));

          const res = await requestLetterApproval(letterId);

          if (res?.code === 200) {
            notify(
              "success",
              res?.message || "Approval requested successfully.",
            );

            // Refresh list
            getLetterList(
              dispatch,
              pageNum,
              buildFilterParams(),
              filterState.searchName,
            );
          } else {
            notify("danger", res?.message || "Failed to request approval.");
          }
        } catch (error) {
          notify("danger", "Failed to request approval.");
        } finally {
          setAlternative((prev) => ({
            ...prev,
            showLoaderOnClick: false,
          }));
        }
      },
    });
  };

  const handleGenerateFinalLetter = (letterId) => {
    showAlert({
      title: "Are you sure you want to generate the letter?",
      message:
        "Once generated, this letter cannot be edited or changed. Its status will be marked as ISSUED.",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setIsDownloading((prev) => ({
            ...prev,
            letterGen: true,
          }));

          const res = await generateFinalLetter(letterId);

          if (res?.code === 200) {
            const message = Array.isArray(res?.message)
              ? res?.message[0]?.Msg
              : res?.message || "Jointer deleted successfully";
            notify("success", message);

            // Refresh list
            getLetterList(
              dispatch,
              pageNum,
              buildFilterParams(),
              filterState.searchName,
            );
          } else {
            const message = Array.isArray(res?.message)
              ? res?.message[0]?.Msg
              : res?.message || "Failed to delete jointer";
            notify("danger", message);
          }
        } catch (error) {
          notify("danger", "Failed to generate letter.");
        } finally {
          setIsDownloading((prev) => ({
            ...prev,
            letterGen: false,
          }));
        }
      },
    });
  };

  const buildFilterParams = () => {
    const filters = [];

    if (filterState.documentType) {
      filters.push({
        field: "LetterType",
        condition: "eq",
        filterValues: [filterState.documentType.value],
      });
    }

    if (filterState.documentScope) {
      filters.push({
        field: "LetterScope",
        condition: "eq",
        filterValues: [filterState.documentScope.value],
      });
    }

    if (filterState.referenceNo.trim()) {
      filters.push({
        field: "ReferenceNo",
        condition: "like",
        filterValues: [filterState.referenceNo.trim()],
      });
    }

    if (filterState.issuedToCustomer.trim()) {
      filters.push({
        field: "IssuedToCustomer",
        condition: "like",
        filterValues: [filterState.issuedToCustomer.trim()],
      });
    }

    if (filterState.project.trim()) {
      filters.push({
        field: "Project",
        condition: "like",
        filterValues: [filterState.project.trim()],
      });
    }

    if (filterState.requestOf.trim()) {
      filters.push({
        field: "RequestOf",
        condition: "like",
        filterValues: [filterState.requestOf.trim()],
      });
    }

    if (filterState.agency.trim()) {
      filters.push({
        field: "Agency",
        condition: "like",
        filterValues: [filterState.agency.trim()],
      });
    }

    if (filterState.issueDate[0]) {
      if (filterState.issueDate[1]) {
        // Range selected
        filters.push({
          field: "IssueDate",
          condition: "btw",
          filterValues: [filterState.issueDate[0], filterState.issueDate[1]],
        });
      } else {
        // Single date selected
        filters.push({
          field: "IssueDate",
          condition: "eq",
          filterValues: [filterState.issueDate[0]],
        });
      }
    }

    if (filterState.validTill[0]) {
      if (filterState.validTill[1]) {
        // Range selected
        filters.push({
          field: "ValidTill",
          condition: "btw",
          filterValues: [filterState.validTill[0], filterState.validTill[1]],
        });
      } else {
        // Single date selected
        filters.push({
          field: "ValidTill",
          condition: "eq",
          filterValues: [filterState.validTill[0]],
        });
      }
    }

    if (filterState.status) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }

    return {
      filters: filters.length > 0 ? JSON.stringify(filters) : [],
    };
  };

  //trainee List added sr no. for each item
  const formattedTraineeList = letterDataList?.data?.Letter?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
          (letterDataList?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  useEffect(() => {
    getLetterList(
      dispatch,
      pageNum,
      buildFilterParams(),
      filterState.searchName,
    );
    return () => {
      dispatch({ type: letterList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (letterDataList?.code === 200) {
      setAlternative({
        isLoading: false,
        error: "",
        noRecords: letterDataList?.data?.Letter?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (letterDataList?.code || letterDataList?.message) {
      setAlternative({
        isLoading: false,
        error:
          letterDataList?.message ||
          "Failed to load letters list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [letterDataList]);

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
    },
    {
      Header: "Letter Type",
      accessor: "letterType",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Letter scope",
      accessor: "letterScope",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Reference No",
      accessor: "referenceNo",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Other Information",
      accessor: "otherInfo",
      disableSortBy: true,
      Cell: ({ row }) => {
        const data = row.original;
        const id = data.ID;

        return (
          <>
            {/* Styled Button */}
            <Button
              size="sm"
              color="link"
              id={`otherInfo_${id}`}
              className="p-0"
            >
              <i className="fa fa-info-circle mr-1" />
              Details
            </Button>

            <UncontrolledPopover
              placement="right"
              target={`otherInfo_${id}`}
              trigger="hover focus"
            >
              <PopoverHeader className="text-center">
                Letter Details
              </PopoverHeader>

              <PopoverBody className="custom-popover-body">
                {data?.letterScope !== "INDIVIDUAL" && (
                  <>
                    <div className="d-flex mb-2">
                      <div className="w-50">
                        <small className="text-muted">Project</small>
                        <div className="font-weight-bold">
                          {data.project || "-"}
                        </div>
                      </div>

                      <div className="w-50">
                        <small className="text-muted">Issued To Customer</small>
                        <div className="font-weight-bold">
                          {data.issuedToCustomer || "-"}
                        </div>
                      </div>
                    </div>

                    <hr className="my-2" />
                  </>
                )}

                {data.letterType === "TOOL_LETTER" ? (
                  <div className="d-flex mb-2">
                    <div className="w-50">
                      <small className="text-muted">From Location</small>
                      <div className="font-weight-bold">
                        {data.fromLocation || "-"}
                      </div>
                    </div>

                    <div className="w-50">
                      <small className="text-muted">To Location</small>
                      <div className="font-weight-bold">
                        {data.toLocation || "-"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex mb-2">
                    <div className="w-50">
                      <small className="text-muted">Request Of</small>
                      <div className="font-weight-bold">
                        {data.requestOf || "-"}
                      </div>
                    </div>

                    <div className="w-50">
                      <small className="text-muted">Created At</small>
                      <div className="font-weight-bold">
                        {data.createdAt || "-"}
                      </div>
                    </div>
                  </div>
                )}

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Issued On</small>
                    <div className="font-weight-bold">
                      {data.issueDate || "-"}
                    </div>
                  </div>

                  <div className="w-50">
                    <small className="text-muted">Valid Till</small>
                    <div className="font-weight-bold">
                      {data.validTill || "-"}
                    </div>
                  </div>
                </div>

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Jointer Name</small>

                    {data.LetterUsers?.length ? (
                      <div className="mt-1">
                        {data.LetterUsers.map((user, index) => (
                          <div key={index} className="font-weight-bold">
                            {index + 1}. {user.name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted mt-1">-</div>
                    )}
                  </div>

                  {data.letterType === "TOOL_LETTER" ? (
                    <div className="w-50">
                      <small className="text-muted">Created At</small>
                      <div className="font-weight-bold">
                        {data.createdAt || "-"}
                      </div>
                    </div>
                  ) : (
                    <div className="w-50">
                      <small className="text-muted">Government ID</small>

                      {data.LetterUsers?.length ? (
                        <div className="mt-1">
                          {data.LetterUsers.map((user, index) => (
                            <div key={index} className="font-weight-bold">
                              {user.governmentID || "-"}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted mt-1">-</div>
                      )}
                    </div>
                  )}
                </div>

                <hr className="my-2" />

                <div className="d-flex mb-2">
                  <div className="w-50">
                    <small className="text-muted">Created By</small>
                    <div className="font-weight-bold">
                      {data.createdByName || "-"}
                    </div>
                  </div>

                  {data.approvedBy > 0 && <div className="w-50">
                    <small className="text-muted">{data.status === "APPROVED" ? "Approved By" : data.status === "REJECTED" ? "Rejected By" : "Approval Action By"}</small>
                    <div className="font-weight-bold">
                      {data.approvedByName || "-"}
                    </div>
                  </div>}
                </div>
              </PopoverBody>
            </UncontrolledPopover>
          </>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge color={getStatusColor(value)} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Rejection Reason",
      accessor: "rejectionReason",
      minWidth: 220,
      maxWidth: 350,
      Cell: ({ value }) => <span className="text-wrapper">{value || "-"}</span>,
    },
    {
      Header: "View Letter",
      accessor: "referenceNoPath",
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const id = row.original.ID;

        return value ? (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`preview${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handlePreviewLetter(
                  row.original.referenceNo,
                  row.original.status,
                );
              }}
            >
              <i className="fa fa-eye mr-1"></i>
              <span>Preview</span>
            </Button>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      Header: "QR Code",
      accessor: "documentQrPath",
      disableSortBy: true,
      Cell: ({ value, row }) => {
        const id = row.original.ID;
        return value ? (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`download${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                e.stopPropagation();
                handleDownloadQR(row.original.encodedReferenceNo);
              }}
            >
              <i class="fa-solid fa-download"></i>
              <span>Download</span>
            </Button>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      Header: "Actions",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { status, ID } = row.original;
        const encodedId = urlEncodedId(ID);

        return (
          <div className="d-flex align-items-center gap-3">
            {/* Edit for Draft + Rejected */}
            {(status === "REJECTED" || status === "DRAFT") && (
              <Button
                className="btn-link d-flex align-items-center p-0 mr-3"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    role === "SUPER_ADMIN"
                      ? `/super-admin/letters/edit-letter/${encodedId}`
                      : `/admin/letters/edit-letter/${encodedId}`,
                    {
                      state: { mode: "edit" },
                    }
                  );
                }}
              >
                <i className="fa fa-edit mr-1 table-action-info" /> Edit
              </Button>
            )}

            {/* Request Approval only for Draft */}
            {status === "DRAFT" && (
              <>
                <Button
                  className="btn-link d-flex align-items-center p-0 mr-3"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRequestApproval(ID);
                  }}
                >
                  <i className="fa fa-paper-plane mr-1" /> Request Approval
                </Button>

                <Button
                  className="btn-link d-flex align-items-center p-0"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLetter(ID);
                  }}
                >
                  <i className="fa fa-trash mr-1" /> Delete
                </Button>
              </>
            )}

            {/* Generate for Approved */}
            {status === "APPROVED" && (
              <Button
                className="btn-link d-flex align-items-center p-0"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateFinalLetter(ID);
                }}
              >
                <i className="fa fa-file-alt mr-1" />
                Generate
              </Button>
            )}

            {/* For other statuses */}
            {!["DRAFT", "REJECTED", "APPROVED"].includes(status) && "-"}
          </div>
        );
      },
    },
  ];

  const handleDownloadQR = async (ID) => {
    setIsDownloading((prev) => ({ ...prev, QR: true }));
    try {
      const res = await downloadLetterQR(ID);
      if (res.status === 200 && res.data) {
        const fileName =
          res.headers["content-disposition"]?.match(/filename="(.+?)"/)?.[1] ||
          "qr-code.png";

        const url = URL.createObjectURL(res.data);

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } else if (res.code === 404) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter QR code. Please try again.";
        notify("danger", message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter QR code. Please try again.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        "Failed to download authorization letter QR code. Please try again.",
      );
    }
    setIsDownloading((prev) => ({ ...prev, QR: false }));
  };

  const handlePreviewLetter = async (ID, status) => {
    setIsDownloading((prev) => ({ ...prev, QR: true }));
    try {
      const res = await downloadLetter(ID);
      if (res.status === 200 && res.data) {
        const blob = new Blob([res.data], {
          type: "application/pdf",
        });
        const fileURL = URL.createObjectURL(blob);
        const isMobile =
          /Android/i.test(
            navigator.userAgent,
          );
        if (isMobile) {
          // Mobile browsers work better with direct open
          window.open(fileURL, "_blank");
        } else {
          // Desktop preview modal
          setPreviewModal({
            isOpen: true,
            fileUrl: fileURL,
            status: status,
          });
        }
        // setPreviewModal({ isOpen: true, fileUrl: fileURL, status: status });
      } else if (res.code === 404) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter. Please try again.";
        notify("danger", message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message ||
          "Failed to download authorization letter. Please try again.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        "Failed to download authorization letter. Please try again.",
      );
    }
    setIsDownloading((prev) => ({ ...prev, QR: false }));
  };

  const handleDeleteLetter = (letterId) => {
    showAlert({
      title: "Are you sure you want to delete this letter?",
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setAlternative((prev) => ({
            ...prev,
            showLoaderOnClick: true,
          }));

          const res = await deleteLetter(letterId);

          if (res?.code === 200) {
            notify("success", res?.message || "Letter deleted successfully");

            // refresh list
            getLetterList(
              dispatch,
              pageNum,
              buildFilterParams(),
              filterState.searchName,
            );
          } else {
            notify("danger", res?.message || "Failed to delete letter");
          }
        } catch (error) {
          notify("danger", "Failed to delete letter");
        } finally {
          setAlternative((prev) => ({
            ...prev,
            showLoaderOnClick: false,
          }));
        }
      },
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={getStatusColor(props?.data?.value)}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={getStatusColor(props?.data?.value)}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const clearSearch = () => {
    setFilterState({
      documentType: "",
      documentScope: "",
      referenceNo: "",
      issuedToCustomer: "",
      project: "",
      requestOf: "",
      agency: "",
      issueDate: "",
      validTill: "",
      status: null,
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    const newFilters = buildFilterParams();

    if (!newFilters.filters.length) return;

    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newFilters)
    ) {
      setAlternative((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilters,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  //role based component display for add
  const handleAdd = () => {
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/letters/create-letter`
        : `/admin/letters/create-letter`,
      {
        state: { mode: "add" },
      }
    );
  };

  return (
    <>
      <AdminHeader />
      {(isDownloading.QR || isDownloading.letterGen) && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              {isDownloading.letterGen
                ? "Generating the letter, this may take a few seconds."
                : "Loading… Please wait."}
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0">
                  Letters
                </CardTitle>

                <Button className="btn-deep-blue" onClick={handleAdd}>
                  <i className="fa fa-plus mr-1" />
                  Generate Letter
                </Button>
              </CardHeader>

              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Select
                      options={LETTER_TYPE_OPTIONS}
                      styles={fixedWidthCustomStyle()}
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Letter type"
                      value={filterState.documentType}
                      onKeyDown={handleKeyDown}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          documentType: selected,
                        }))
                      }
                    />

                    <Select
                      options={LETTER_SCOPE_FILTER_OPTIONS}
                      styles={fixedWidthCustomStyle()}
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Letter scope"
                      value={filterState.documentScope}
                      onKeyDown={handleKeyDown}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          documentScope: selected,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by Reference No"
                      value={filterState.referenceNo}
                      onKeyDown={handleKeyDown}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          referenceNo: e.target.value,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by customer name"
                      onKeyDown={handleKeyDown}
                      value={filterState.issuedToCustomer}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          issuedToCustomer: e.target.value,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by project name"
                      onKeyDown={handleKeyDown}
                      value={filterState.project}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          project: e.target.value,
                        }))
                      }
                    />

                    <Input
                      className="mr-2 mb-2 filter-input-large"
                      placeholder="Search by Requestor"
                      onKeyDown={handleKeyDown}
                      value={filterState.requestOf}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          requestOf: e.target.value,
                        }))
                      }
                    />

                    <div className="d-flex align-items-center mr-2 mb-2 mt-2">
                      <Label className="mb-0 mr-2 small">Issue Date</Label>
                      <DatePicker
                        selected={filterState.issueDate[0]}
                        onChange={(dates) =>
                          setFilterState((prev) => ({
                            ...prev,
                            issueDate: dates,
                          }))
                        }
                        startDate={filterState.issueDate[0]}
                        endDate={filterState.issueDate[1]}
                        selectsRange
                        onKeyDown={handleKeyDown}
                        isClearable={!!filterState.issueDate[0]}
                        placeholderText="Select issue date or range"
                        className="form-control mr-2 mb-2 filter-input-large"
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>

                    <div className="d-flex align-items-center mr-2 mb-2 mt-2">
                      <Label className="mb-0 mr-2 small">Valid Till</Label>
                      <DatePicker
                        selected={filterState.validTill[0]}
                        onChange={(dates) =>
                          setFilterState((prev) => ({
                            ...prev,
                            validTill: dates,
                          }))
                        }
                        startDate={filterState.validTill[0]}
                        endDate={filterState.validTill[1]}
                        onKeyDown={handleKeyDown}
                        selectsRange
                        isClearable={!!filterState.validTill[0]}
                        placeholderText="Select valid till date or range"
                        className="form-control mr-2 mb-2 filter-input-large"
                        dateFormat="yyyy-MM-dd"
                      />
                    </div>

                    <Select
                      placeholder="Select status"
                      className="mr-2 mb-2 filter-input-large"
                      styles={fixedWidthCustomStyle()}
                      options={LETTER_STATUS_OPTIONS}
                      value={filterState.status}
                      onKeyDown={handleKeyDown}
                      components={{ Option, SingleValue }}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          status: selected,
                        }))
                      }
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                      onClick={handleSearch}
                    >
                      {alternative.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams?.filters?.length > 0 && (
                      <Button
                        color="default"
                        className="d-flex align-items-center mb-2 ml-0 filter-btn btn-deep-blue px-4"
                        onClick={clearSearch}
                      >
                        <span>
                          {" "}
                          <i className="fa fa-times mr-1" /> Clear
                        </span>
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {letterDataList?.data?.totalRecords ? (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ? (
                        <span className="font-weight-bold">
                          Showing {letterDataList?.data?.totalFilteredRecords}{" "}
                          of {letterDataList?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {letterDataList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {alternative.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : alternative.error ? (
                  <>
                    <div className="loaderContainer">{alternative.error}</div>
                  </>
                ) : alternative.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No trainees found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={formattedTraineeList || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        letterDataList?.data?.totalFilteredRecords /
                        letterDataList?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <CommonModal
        isOpen={previewModal.isOpen}
        toggle={() =>
          setPreviewModal({ isOpen: false, fileUrl: "", status: "" })
        }
        title="Letter Preview"
        size="xl"
        height="80vh"
      >
        {previewModal.fileUrl && (
          <iframe
            src={
              previewModal.status !== "ISSUED"
                ? `${previewModal.fileUrl}#toolbar=0&navpanes=0&scrollbar=0`
                : previewModal.fileUrl
            }
            title="PDF Preview"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        )}
      </CommonModal>
    </>
  );
}

export default LetterListing;
